import { DEFAULT_PAX } from "@booking/consts";
import { isProductionOrStaging } from "@booking/utils/environment";
import { type StateCreator, create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

import { createAppSettingState } from "./appSetting/createAppSettingState";
import { createPaymentState } from "./payment/createPaymentState";
import { createRoomsState } from "./rooms/createRoomsState";
import { Room } from "./rooms/types";
import { createRoundsState } from "./rounds/createRoundsState";
import { createSimpleTeeTimesState } from "./simpleTeeTimes/createSimpleTeeTimesState";
import type { BookingPersist, BookingState } from "./types";

const PERSIST_VERSION = 2;

const createBookingState: StateCreator<BookingState> = (...args) => ({
  ...createRoomsState(...args),
  ...createRoundsState(...args),
  ...createSimpleTeeTimesState(...args),
  ...createAppSettingState(...args),
  ...createPaymentState(...args),
});

const devToolsOptions = { enabled: !isProductionOrStaging() };

const migrate = (persistedState: any, version: number) => {
  const rooms: Room[] = Array.isArray(persistedState?.rooms)
    ? persistedState.rooms
    : [];

  if (version < PERSIST_VERSION && rooms.length === 1) {
    const [room] = rooms;
    return {
      ...persistedState,
      rooms: [{ ...room, pax: Math.max(room.pax, DEFAULT_PAX) }],
    };
  }

  return { ...persistedState, rooms };
};

const persistOptions = {
  name: "ygt-booking",
  version: PERSIST_VERSION,
  partialize: ({ rooms }: BookingState): BookingPersist => ({
    rooms,
  }),
  storage: createJSONStorage(() => localStorage),
  migrate,
};

export const useBookingStore = create<BookingState>()(
  devtools(persist(createBookingState, persistOptions), devToolsOptions),
);
